import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { WORKSPACE_STORAGE_KEY } from '@/constants/workspace';
import {
  getCurrentSubscription,
  getSubscriptionHistory,
  getModuleSubscriptions,
} from '@/services/subscription';
import { getWorkspaceCredits } from '@/services/workspaces';
import type { IWorkspaceCreditsV3 } from '@/types/workspace';

export interface Subscription {
  id: string;
  workspace_id: number;
  status: 'active' | 'inactive' | 'trial';
  // 'trial' is the legacy plan value retired by backend migration 20260528 —
  // kept in the union so older rows still type-check, but new trial rows
  // carry the chosen tier here (status='trial' is the lifecycle marker).
  plan: 'enterprise' | 'prospect' | 'outreach' | 'growth' | 'starter' | 'standard' | 'pro' | 'trial';
  // True for any row that started life as a trial — survives status='inactive'.
  // Used to decide "trial already used" without depending on plan value.
  started_as_trial?: boolean;
  seat_count: number;
  current_period_start: string;
  current_period_end: string | null;
  billing_contact: string | null;
  billing_note: string | null;
  stripe_customer_id: string | null;
  stripe_subscription_id: string | null;
  stripe_payment_method_id: string | null;
  created_at: string;
  updated_at: string;
  workspace_name?: string;
  product_module: 'sales' | 'marketing' | null;
  // Credit balances
  enrichment_credit_balance: number;
  ai_credit_balance: number;
  video_avatar_credit_balance?: number;
  enterprise_ai_unlimited: boolean;
  enrichment_carry_forward_balance: number;
  ai_carry_forward_balance: number;
  // Company reveal (enterprise dedicated pool — GH#1094)
  company_reveal_balance: number;
  company_reveal_monthly_allowance: number | null;
  // Enrichment auto-recharge
  auto_topup_enabled: boolean;
  auto_topup_threshold: number;
  auto_topup_package: 'starter' | 'growth' | 'pro' | null;
  // AI auto-recharge
  ai_auto_topup_enabled: boolean;
  ai_auto_topup_threshold: number;
  ai_auto_topup_package: 'small' | 'medium' | 'large';
  // Avatar auto-recharge
  avatar_auto_topup_enabled: boolean;
  avatar_auto_topup_threshold: number;
  avatar_auto_topup_package: 'starter' | 'growth' | 'pro' | null;
  // Dialer
  dialer_minutes_limit: number | null;
  // Cancellation
  cancel_at_period_end: boolean;
  canceled_at: string | null;
}

interface SubscriptionResponse {
  success: boolean;
  data: Subscription | null;
}

interface SubscriptionHistoryResponse {
  success: boolean;
  data: Subscription[];
}

interface ModuleSubscriptionsResponse {
  success: boolean;
  data: { sales: Subscription | null; marketing: Subscription | null };
}

/** Query keys for subscription domain */
export const subscriptionKeys = {
  all: ['subscription'] as const,
  current: () => [...subscriptionKeys.all, 'current'] as const,
  modules: () => [...subscriptionKeys.all, 'modules'] as const,
  history: () => [...subscriptionKeys.all, 'history'] as const,
  credits: (workspaceId?: number) => [...subscriptionKeys.all, 'credits', workspaceId] as const,
};

// ─── Query Hooks ─────────────────────────────────────────────────────────────

/** Per-module subscription state — { sales, marketing } each null or a Subscription row */
export const useModuleSubscriptions = (options?: { enabled?: boolean }) => {
  return useQuery<ModuleSubscriptionsResponse>({
    queryKey: subscriptionKeys.modules(),
    queryFn: getModuleSubscriptions as () => Promise<ModuleSubscriptionsResponse>,
    staleTime: 2 * 60 * 1000,
    gcTime: 5 * 60 * 1000,
    retry: false,
    throwOnError: false,
    enabled: options?.enabled ?? true,
  });
};

export const useCurrentSubscription = (options?: {
  enabled?: boolean;
  staleTime?: number;
  gcTime?: number;
}) => {
  return useQuery<SubscriptionResponse>({
    queryKey: subscriptionKeys.current(),
    queryFn: getCurrentSubscription,
    staleTime: options?.staleTime ?? 5 * 60 * 1000,
    gcTime: options?.gcTime ?? 10 * 60 * 1000,
    retry: false,
    throwOnError: false,
    enabled: options?.enabled ?? true,
  });
};

/**
 * Workspace id from the caller or local storage. Mirrors the resolution already
 * used by useWorkspaceCreditsQuery so workspace-scoped queries stay consistent.
 */
export const resolveWorkspaceId = (explicitWorkspaceId?: number): number | undefined => {
  if (explicitWorkspaceId) return explicitWorkspaceId;
  if (typeof window !== 'undefined') {
    const storedWorkspaceId = localStorage.getItem(WORKSPACE_STORAGE_KEY);
    if (storedWorkspaceId && storedWorkspaceId !== 'null' && storedWorkspaceId !== 'undefined') {
      return parseInt(storedWorkspaceId, 10);
    }
  }
  return undefined;
};

export const useSubscriptionHistory = (options?: {
  enabled?: boolean;
  staleTime?: number;
  gcTime?: number;
}) => {
  // Without a workspace the backend throws NO_ACTIVE_WORKSPACE (403). The error
  // was already swallowed here (retry/throwOnError false), so callers saw
  // `undefined` either way — but every render on /onboarding/welcome and
  // /dashboard still burned a request that landed in prod logs as a 403 on a
  // private endpoint, reading like an authorization defect. Gating on the
  // workspace, as useWorkspaceCreditsQuery already does, keeps the resolved data
  // identical and drops the misleading noise.
  const hasWorkspace = !!resolveWorkspaceId();

  return useQuery<SubscriptionHistoryResponse>({
    queryKey: subscriptionKeys.history(),
    queryFn: getSubscriptionHistory,
    staleTime: options?.staleTime ?? 5 * 60 * 1000,
    gcTime: options?.gcTime ?? 10 * 60 * 1000,
    enabled: (options?.enabled ?? true) && hasWorkspace,
    retry: false,
    throwOnError: false,
  });
};

/**
 * True when this workspace had a marketing subscription (trial or paid) that is no
 * longer active/trial, and never had a sales subscription. Derived from subscription
 * HISTORY so it survives `setInactive()` stripping `'marketing'` from product_modules
 * (which flips `isMarketingOnly` false post-expiry). Use this — OR'd with isMarketingOnly
 * — to keep post-expiry surfaces (no-subscription screen, billing offer) marketing-aware
 * so they drive the user to book a meeting with Sunder, not the sales Stripe picker.
 * Mirrors the detection in MarketingTrialExpiredOverlay.
 */
export const useIsMarketingExpired = (options?: { enabled?: boolean }): boolean => {
  const { data } = useSubscriptionHistory({ enabled: options?.enabled ?? true });
  return useMemo(() => {
    const subs = data?.data ?? [];
    if (subs.length === 0) return false;
    const hadMarketing = subs.some((s) => s.product_module === 'marketing');
    if (!hadMarketing) return false;
    const hasSalesEver = subs.some((s) => s.product_module === 'sales');
    if (hasSalesEver) return false;
    const hasActiveMkt = subs.some(
      (s) => s.product_module === 'marketing' && (s.status === 'active' || s.status === 'trial'),
    );
    return !hasActiveMkt;
  }, [data]);
};

export const useWorkspaceCreditsQuery = (options?: {
  enabled?: boolean;
  staleTime?: number;
  gcTime?: number;
  workspaceId?: number;
}) => {
  const resolvedWorkspaceId = resolveWorkspaceId(options?.workspaceId);

  return useQuery({
    queryKey: subscriptionKeys.credits(resolvedWorkspaceId),
    queryFn: () => {
      if (!resolvedWorkspaceId) {
        throw new Error('Workspace ID is required for fetching credits');
      }
      return getWorkspaceCredits(resolvedWorkspaceId);
    },
    staleTime: options?.staleTime ?? 1 * 60 * 1000,
    gcTime: options?.gcTime ?? 5 * 60 * 1000,
    retry: false,
    throwOnError: false,
    enabled: (options?.enabled ?? true) && !!resolvedWorkspaceId,
  });
};

/** Combined hook — fetches current subscription, history, and credits in parallel */
export const useWorkspaceSubscriptionsOptimized = (options?: {
  enabled?: boolean;
  workspaceId?: number;
}) => {
  const enabled = options?.enabled ?? true;

  const {
    data: currentSub,
    isLoading: loadingCurrent,
    error: currentError,
  } = useCurrentSubscription({
    enabled,
    staleTime: 10 * 60 * 1000,
    gcTime: 15 * 60 * 1000,
  });

  const {
    data: history,
    isLoading: loadingHistory,
    error: historyError,
  } = useSubscriptionHistory({
    enabled,
    staleTime: 10 * 60 * 1000,
    gcTime: 15 * 60 * 1000,
  });

  const {
    data: credits,
    isLoading: loadingCredits,
    error: creditsError,
  } = useWorkspaceCreditsQuery({
    enabled,
    staleTime: 2 * 60 * 1000,
    gcTime: 5 * 60 * 1000,
    workspaceId: options?.workspaceId,
  });

  const subscriptions = history?.data || [];
  const hasActiveSubscription = subscriptions.some(
    (sub) => sub.status === 'active' || sub.status === 'trial',
  );

  const stripeSubscription = subscriptions.find(
    (sub) => sub.plan === 'enterprise' && sub.stripe_customer_id !== null,
  );

  const rawCredits = credits?.credits as IWorkspaceCreditsV3 | null | undefined;

  return {
    data: {
      workspace_name: currentSub?.data?.workspace_name || '',
      subscriptions,
      hasActiveSubscription,
      stripe_customer_id: stripeSubscription?.stripe_customer_id || null,
      credits: rawCredits ?? null,
    },
    isLoading: loadingCurrent || loadingHistory || loadingCredits,
    error: currentError || historyError || creditsError,
  };
};
